// MST
import { types, cast, flow, detach } from 'mobx-state-tree'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_paginationProps } from 'models/extensions/with-helper'

// Interfaces
import {
  IUserAccessApiListResponse,
  IUserAccessApiDetailResponse,
  IUserAccessApiNullResponse,
} from 'interfaces/back-office/user-access/user-access-api.interface'
import {
  IUserAccessGetListAttrs,
  IUserAccessGetDetailAttrs,
  IUserAccessUpdateAttrs,
} from 'interfaces/back-office/user-access/user-access-view.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Lodash
import omit from 'lodash/omit'

// Utils
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Constant
import { LOADING } from 'constant/shared.constant'

// Types
const { model, optional, number, string, array, boolean } = types

// Loading
const { BASE, DETAIL, ACTION } = LOADING

// User Access Model
const UserAccess = model('UserAccess').props({
  code: optional(number, 0),
  name: optional(string, ''),
  module: optional(string, ''),
  hasAccess: optional(boolean, false)
})

// User Access Props
const userAccessProps = {
  number: optional(number, 0),
  roleId: optional(number, 0),
  roleName: optional(string, ''),
  access: optional(array(UserAccess), []),
}

// User Access List Model
const UserAccessList = optional(array(model('UserAccessList').props(omit(userAccessProps, ['access']))), [])

// User Access Paginated Model
const UserAccessPaginated = model('UserAccessPaginated').props({ ...withHelper_paginationProps, list: UserAccessList })

// User Access Detail Model
const UserAccessDetail = model('UserAccessDetail').props(omit(omit(userAccessProps, ['number'])))

// Base Model
const UserAccessBase = types
  .model('UserAccessBase')
  .props({
    userAccess_loading: withHelper_commonLoadingModel,
    userAccess_list: optional(UserAccessPaginated, {}),
    userAccess_detail: optional(UserAccessDetail, {}),
  })
  .actions(self => ({
    userAccess_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.userAccess_loading, payload)

      detach(self.userAccess_loading)
      self.userAccess_loading = cast(map)
    },
    userAccess_SET_ROLE_ACCESS_LIST: (payload: IUserAccessApiListResponse['res']['data']): void => {
      self.userAccess_list = cast({
        ...payload,
        list: payload.list.map((list, index) => ({
          ...list,
          number: commonUtils_renderTableNumber(index, payload),
        })),
      })
    },
    userAccess_SET_ROLE_ACCESS_DETAIL: (payload: IUserAccessApiDetailResponse['res']['data']): void => {
      self.userAccess_detail = cast(payload)
    },
    userAccess_CLEAR_LIST: (): void => {
      self.userAccess_list = cast({})
    },
    userAccess_CLEAR_DETAIL: (): void => {
      self.userAccess_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch user access list
     *
     * @param {IUserAccessGetListAttrs} payload
     *
     * @return {any} any
     */
    userAccess_fetchList: flow(function* (payload: IUserAccessGetListAttrs) {
      self.userAccess_SET_LOADING({ type: BASE, value: true })

      const response: IUserAccessApiListResponse = yield self.environment.userAccessApi.userAccess_getList({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.userAccess_SET_ROLE_ACCESS_LIST(response.res.data)

      self.userAccess_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch user access
     *
     * @param {IUserAccessGetDetailAttrs} payload
     *
     * @return {any} any
     */
    userAccess_fetchDetail: flow(function* (payload: IUserAccessGetDetailAttrs) {
      self.userAccess_SET_LOADING({ type: DETAIL, value: true })

      const response: IUserAccessApiDetailResponse = yield self.environment.userAccessApi.userAccess_getDetail({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.userAccess_SET_ROLE_ACCESS_DETAIL(response.res.data)

      self.userAccess_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Update user access
     *
     * @param {IUserAccessUpdateAttrs} payload
     *
     * @return {any} any
     */
    userAccess_update: flow(function* (payload: IUserAccessUpdateAttrs) {
      self.userAccess_SET_LOADING({ type: ACTION, value: true })

      const response: IUserAccessApiNullResponse = yield self.environment.userAccessApi.userAccess_update({
        params: { id: payload.params.id },
        body: payload.body,
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.userAccess_SET_LOADING({ type: ACTION, value: false })
    }),
  }))

export const createUserAccessStoreDefaultModel = () => optional(UserAccessBase, {})
